import React, { useState, useEffect } from 'react';
import '../crm.css';

const typeOptions = [
    { value: 'call', label: 'Call' },
    { value: 'email', label: 'Email' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'task', label: 'Task' },
    { value: 'note', label: 'Note' }
];

const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
];

const entityTypeOptions = [
    { value: 'lead', label: 'Lead' },
    { value: 'contact', label: 'Contact' },
    { value: 'deal', label: 'Deal' }
];

const ActivityForm = ({ activity, onSubmit, onCancel, loading }) => {
    const [formData, setFormData] = useState({
        type: 'call',
        title: '',
        description: '',
        status: 'pending',
        dueDate: '', // Date part
        dueTime: '', // Time part
        duration: 0,
        entityType: 'lead',
        entityId: '',
        entityName: '',
        // Call specific
        callDirection: 'outbound',
        callOutcome: '',
        // Meeting specific
        location: '',
        attendees: ''
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (activity) {
            let dDate = '';
            let dTime = '';
            if (activity.dueDate) {
                const dateObj = new Date(activity.dueDate);
                dDate = dateObj.toISOString().split('T')[0];
                dTime = dateObj.toTimeString().slice(0, 5);
            }

            setFormData({
                type: activity.type || 'call',
                title: activity.title || '',
                description: activity.description || '',
                status: activity.status || 'pending',
                dueDate: dDate,
                dueTime: dTime,
                duration: activity.duration || 0,
                entityType: activity.entityType || 'lead',
                entityId: activity.entityId || '',
                entityName: activity.entityName || '',
                callDirection: activity.callDirection || 'outbound',
                callOutcome: activity.callOutcome || '',
                location: activity.location || '',
                attendees: activity.attendees || ''
            });
        }
    }, [activity]);

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.entityId.trim()) newErrors.entityId = 'Entity ID is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            // Combine date and time
            let finalDate = null;
            if (formData.dueDate) {
                finalDate = new Date(`${formData.dueDate}T${formData.dueTime || '00:00:00'}`);
            }

            onSubmit({
                ...formData,
                dueDate: finalDate ? finalDate.toISOString() : null
            });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="crm-form">
            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label required">Type</label>
                    <select
                        className="crm-select"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        disabled={loading}
                    >
                        {typeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className="crm-form-group">
                    <label className="crm-label">Status</label>
                    <select
                        className="crm-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        disabled={loading}
                    >
                        {statusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="crm-form-group">
                <label className="crm-label required">Title</label>
                <input
                    type="text"
                    className={`crm-input ${errors.title ? 'error' : ''}`}
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Follow up call"
                    disabled={loading}
                />
                {errors.title && <span className="crm-error">{errors.title}</span>}
            </div>

            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label">Due Date</label>
                    <input
                        type="date"
                        className="crm-input"
                        value={formData.dueDate}
                        onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                        disabled={loading}
                    />
                </div>
                <div className="crm-form-group">
                    <label className="crm-label">Time</label>
                    <input
                        type="time"
                        className="crm-input"
                        value={formData.dueTime}
                        onChange={(e) => setFormData({ ...formData, dueTime: e.target.value })}
                        disabled={loading}
                    />
                </div>
            </div>

            {/* Entity Selection */}
            <div className="crm-form-row">
                <div className="crm-form-group">
                    <label className="crm-label required">Related To</label>
                    <select
                        className="crm-select"
                        value={formData.entityType}
                        onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
                        disabled={loading}
                    >
                        {entityTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className="crm-form-group">
                    <label className="crm-label required">ID / Name</label>
                    <input
                        type="text"
                        className={`crm-input ${errors.entityId ? 'error' : ''}`}
                        value={formData.entityId}
                        onChange={(e) => setFormData({ ...formData, entityId: e.target.value })}
                        placeholder="Entity ID (Temporary)"
                        disabled={loading}
                    />
                    {errors.entityId && <span className="crm-error">{errors.entityId}</span>}
                </div>
            </div>
            <div className="crm-form-group">
                <label className="crm-label">Entity Name (Display)</label>
                <input
                    type="text"
                    className="crm-input"
                    value={formData.entityName}
                    onChange={(e) => setFormData({ ...formData, entityName: e.target.value })}
                    placeholder="e.g. John Doe"
                    disabled={loading}
                />
            </div>

            {/* Type Specific Fields */}
            {formData.type === 'meeting' && (
                <div className="crm-form-group">
                    <label className="crm-label">Location</label>
                    <input
                        type="text"
                        className="crm-input"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        disabled={loading}
                    />
                </div>
            )}

            <div className="crm-form-group">
                <label className="crm-label">Description</label>
                <textarea
                    className="crm-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    disabled={loading}
                />
            </div>

            <div className="crm-form-actions">
                <button
                    type="button"
                    className="crm-btn crm-btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="crm-btn crm-btn-primary"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save Activity'}
                </button>
            </div>
        </form>
    );
};

export default ActivityForm;
